import React from 'react';
import { Box, Button, Typography } from '@mui/material';

interface LegendItem {
  label: string;
  color: string;
  value: number;
  percentage: number;
}

interface ChartLegendProps {
  items: LegendItem[];
  orientation?: 'vertical' | 'horizontal';
  showPercentages?: boolean;
}

export function ChartLegend({ items, orientation = 'vertical', showPercentages = true }: ChartLegendProps) {
  const maxItems = orientation === 'vertical' ? 10 : 5;
  const [expanded, setExpanded] = React.useState(false);
  
  const visibleItems = expanded ? items : items.slice(0, maxItems);
  const hiddenCount = items.length - maxItems;
  const shouldTruncate = hiddenCount > 0;
  
  if (items.length === 0) return null;
  
  return (
    <Box
      sx={{
        display: orientation === 'vertical' ? 'grid' : 'flex',
        gridTemplateColumns: orientation === 'vertical' ? 'repeat(auto-fill, minmax(180px, 1fr))' : undefined,
        flexWrap: orientation === 'horizontal' ? 'wrap' : undefined,
        gap: orientation === 'vertical' ? 1.5 : 2,
        mt: 2,
        px: 2,
        columnGap: orientation === 'vertical' ? 3 : 2,
      }}
    >
      {visibleItems.map((item, idx) => (
        <Box
          key={idx}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: item.color,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.8125rem',
              color: 'text.secondary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
          >
            {item.label}
            {showPercentages && ` (${item.percentage.toFixed(0)}%)`}
          </Typography>
        </Box>
      ))}
      
      {shouldTruncate && (
        <Button
          size="small"
          onClick={() => setExpanded(!expanded)}
          sx={{
            alignSelf: orientation === 'vertical' ? 'flex-start' : 'center',
            textTransform: 'none',
            fontSize: '0.75rem',
            minWidth: 'auto',
            p: 0.5,
            color: 'primary.main',
          }}
        >
          {expanded ? 'Show less' : `Show ${hiddenCount} more`}
        </Button>
      )}
    </Box>
  );
}

export function extractLegendData(spec: any, data: any[], colorMap?: Map<string, string>): LegendItem[] | null {
  if (!spec || !data || data.length === 0) return null;
  
  try {
    const encoding = spec.encoding || {};
    const colorField = encoding.color?.field;
    
    if (!colorField) return null;
    
    const colorType = encoding.color?.type?.toLowerCase();
    const isNominalOrOrdinal = colorType === 'nominal' || colorType === 'ordinal';
    
    if (!isNominalOrOrdinal) return null;
    
    const colorScale = encoding.color?.scale;
    const colorRange = colorScale?.range || spec.config?.range?.category || [];
    
    // Find the quantitative value field (theta for pie, y/x for bar, size, etc.)
    const valueField = 
      encoding.theta?.field || 
      encoding.y?.field || 
      encoding.x?.field || 
      encoding.size?.field ||
      null;
    
    const groupedData = new Map<string, number>();
    
    if (valueField) {
      // Sum values by category
      data.forEach(row => {
        const rawValue = row[colorField];
        const key = rawValue === null || rawValue === undefined || rawValue === 'null' || rawValue === 'undefined' 
          ? 'N/A' 
          : String(rawValue);
        const value = Number(row[valueField]) || 0;
        groupedData.set(key, (groupedData.get(key) || 0) + value);
      });
    } else {
      // Fallback: count rows
      data.forEach(row => {
        const rawValue = row[colorField];
        const key = rawValue === null || rawValue === undefined || rawValue === 'null' || rawValue === 'undefined'
          ? 'N/A'
          : String(rawValue);
        groupedData.set(key, (groupedData.get(key) || 0) + 1);
      });
    }
    
    // Calculate total
    let total = 0;
    groupedData.forEach(value => {
      total += value;
    });
    
    const items: LegendItem[] = [];
    
    let colorIdx = 0;
    groupedData.forEach((value, label) => {
      // Use colorMap from Vega view if available, otherwise fall back to sequential assignment
      const color = colorMap?.get(label) ||
        (Array.isArray(colorRange) && colorRange[colorIdx % colorRange.length]
          ? colorRange[colorIdx % colorRange.length]
          : `hsl(${(colorIdx * 137.5) % 360}, 70%, 50%)`);
      
      items.push({
        label,
        color,
        value,
        percentage: total > 0 ? (value / total) * 100 : 0,
      });
      colorIdx++;
    });
    
    items.sort((a, b) => b.value - a.value);
    
    return items;
  } catch (err) {
    console.error('Failed to extract legend data:', err);
    return null;
  }
}
